import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getLevel($filter: levelFilterInput) {
    getLevel(filter: $filter) {
      id
      name
      parentLevelId
      allowKitAssign
      childLevelList {
        allowKitAssign
        name
        id
      }
      levelAttributeList {
        optionList
        fieldType
        sortOrder
        name
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.LevelState = {
  result: null,
  error: null,
  loading: false
};

export const getLevelRequest = createAsyncThunk<
  TYPES.Level,
  {filter?: TYPES.LevelFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'level/get_level',
  async (params: {filter?: TYPES.LevelFilterInput;}, { extra: client }) => {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params,
        fetchPolicy: "network-only"
      });
      return response.data.getLevel;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getLevelSlice = createSlice({
  name: 'get_level',
  initialState: initialState,
  reducers: {
    getLevelClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getLevelRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getLevelRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getLevelRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getLevelClear } = getLevelSlice.actions;

export const getLevel = createSelector(
  (state: RootState) => state.level.getLevel.result,
  (state: RootState) => state.level.getLevel.error,
  (state: RootState) => state.level.getLevel.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getLevelSlice.reducer;